import { useEffect, useRef, useState } from 'react'
import type { ECElementEvent, EChartsCoreOption, EChartsType } from 'echarts/core'
import { validateChartDefinition, type ChartDefinition as GovernedChartDefinition } from '@solum/experience'
import s from './InsightChart.module.css'
import { chartValueFormatter, formatTimeBucket } from './chartFormat'

export type InsightDatum = Readonly<Record<string, unknown>>

/**
 * The kinds this adapter draws (#1188 slice 2).
 *
 * `sparkline`, `bullet` and `waterfall` are COMPOSED from the Line and Bar
 * series already registered in `echartsEngine.ts` — they cost nothing in the
 * lazy chunk, which is the whole reason that file registers concrete series
 * instead of the barrel.
 *
 * `scatter`, `heatmap` and `funnel` are deliberately still refused. Each needs
 * its own ECharts series registered, and heatmap additionally needs
 * `VisualMapComponent`; that is a bundle decision about the renderer chunk
 * rather than a rendering one, and it should be taken deliberately rather than
 * arrive as a side effect of adding a kind. The contract keeps declaring them,
 * the door keeps accepting them, and this adapter keeps refusing them BY NAME —
 * which is exactly what the refusal is for.
 */
const SUPPORTED = new Set(['line', 'area', 'bar', 'stacked_bar', 'sparkline', 'bullet', 'waterfall'])
const DEFAULT_PALETTE = ['#2584ff', '#24a47a', '#a26be8', '#e59a2f', '#dd5c69', '#49a9b8']

let engine: Promise<typeof import('echarts/core')> | null = null

const loadEngine = () => {
  engine ??= import('./echartsEngine')
    .then(({ echarts }) => echarts)
    .catch((error: unknown) => {
      // A transient chunk failure must not poison every later chart mount.
      engine = null
      throw error
    })
  return engine
}

/**
 * The complete boundary between the governed grammar and this adapter build.
 * Hosts call it before mounting the lazy engine; buildChartOption calls it too
 * so a host that forgets the preflight still fails closed by name.
 */
export function chartAdapterProblem(value: unknown): string | null {
  const issues = validateChartDefinition(value)
  if (issues.length > 0) return `Invalid chart definition: ${issues.join('; ')}`
  const definition = value as ChartDefinition
  if (!SUPPORTED.has(definition.kind)) {
    return `InsightChart does not render ${definition.kind} in adapter version 1`
  }
  try {
    assertComposedEncoding(definition)
  } catch (error) {
    return error instanceof Error ? error.message : 'Invalid composed chart definition'
  }
  if (!definition.encoding.timeField && !definition.encoding.dimension) {
    return 'InsightChart needs a governed time or dimension encoding'
  }
  return null
}

const key = (value: unknown) => value == null ? 'Not reported' : String(value)
/** A governed field name read as words: `seats_used` -> `seats used`. */
const fieldWords = (field: string) => field.replaceAll(/[._-]+/gu, ' ')
const number = (value: unknown): number | null => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const compare = (left: unknown, right: unknown) => {
  if (left == null && right == null) return 0
  if (left == null) return 1
  if (right == null) return -1
  if (typeof left === 'number' && typeof right === 'number') return left - right
  return String(left).localeCompare(String(right), undefined, { numeric: true })
}

const orderedRows = (definition: ChartDefinition, rows: readonly InsightDatum[]) => {
  if (!definition.sort) return rows
  const field = definition.sort.by === 'measure'
    ? definition.encoding.measure
    : definition.sort.by === 'time'
      ? definition.encoding.timeField
      : definition.encoding.dimension
  if (!field) return rows
  const direction = definition.sort.direction === 'desc' ? -1 : 1
  return [...rows].sort((left, right) => compare(left[field], right[field]) * direction)
}

/** Resolve an ECharts series point back to the governed source row it depicts. */
export function rowForChartPoint(
  definition: ChartDefinition,
  rows: readonly InsightDatum[],
  point: Pick<ECElementEvent, 'componentType' | 'dataIndex' | 'seriesIndex'>,
): InsightDatum | null {
  const { dataIndex, seriesIndex: pointSeriesIndex } = point
  if (point.componentType !== 'series'
      || typeof dataIndex !== 'number' || !Number.isInteger(dataIndex)
      || typeof pointSeriesIndex !== 'number' || !Number.isInteger(pointSeriesIndex)) return null
  const categoryField = definition.encoding.timeField ?? definition.encoding.dimension
  if (!categoryField) return null
  const input = orderedRows(definition, rows).slice(0, definition.limit ?? 500)
  const categories = [...new Set(input.map((row) => key(row[categoryField])))]
  const category = categories[dataIndex]
  if (category === undefined) return null

  const seriesField = definition.encoding.series
  const groups = seriesField
    ? [...new Set(input.map((row) => key(row[seriesField])))]
    : ['']
  const measureCount = 1
    + (definition.encoding.comparisonMeasure ? 1 : 0)
    + (definition.encoding.baselineMeasure ? 1 : 0)
  // Waterfall inserts one silent running-base series before its sole delta.
  const seriesIndex = definition.kind === 'waterfall'
    ? pointSeriesIndex - 1
    : pointSeriesIndex
  if (seriesIndex < 0) return null
  const group = groups[Math.floor(seriesIndex / measureCount)]
  if (group === undefined) return null
  return input.find((row) => key(row[categoryField]) === category
    && (!seriesField || key(row[seriesField]) === group)) ?? null
}

/**
 * THE KIT'S INK, resolved for a renderer that cannot read CSS (#2406).
 *
 * ECharts paints its axes, grid and tooltip from its own defaults — the
 * platform's fallback face ("Microsoft YaHei" on Windows), a #54555a label ink
 * that vanished on the dark ground, near-white grid lines across a dark card
 * and a white tooltip on every theme. Its SVG writes each value INLINE
 * (`style="font: …"`, `fill="…"`), so no stylesheet can reach it, and it
 * measures text on a canvas with the family it was GIVEN, so an unresolved
 * `var(--font-sans)` would also measure wrong. So the tokens are resolved on
 * the chart's own element — where a brand, a mode or a cap has set them — and
 * handed over as values; `InsightChart` resolves them again when the theme
 * flips. Every value is a kit token; an absent one leaves that part to the
 * engine rather than inventing a colour.
 */
export interface ChartInk {
  /** `--font-sans`: every word the chart draws. */
  font?: string
  /** `--muted`: axis labels, legend, the axis line — secondary text. */
  text?: string
  /** `--ink`: the figures a person reads off the tooltip. */
  strong?: string
  /** `--hair`: grid lines and ticks, the same joint every table draws. */
  line?: string
  /** `--fs-xs` in px: axis and legend labels. */
  size?: number
  /** `--fs-sm` in px: the tooltip, as the kit's `Tooltip` sets it. */
  tipSize?: number
  /** `--surface`: the tooltip's plate. */
  plate?: string
  /** `--r-sm` in px: the tooltip's radius. */
  radius?: number
  /** `--clay-2`: the overlay elevation the kit's `Tooltip` carries. */
  shadow?: string
}

const CHART_INK_TOKENS = {
  font: '--font-sans', text: '--muted', strong: '--ink', line: '--hair',
  size: '--fs-xs', tipSize: '--fs-sm', plate: '--surface', radius: '--r-sm', shadow: '--clay-2',
} as const satisfies Record<keyof ChartInk, string>

/** Read the kit's ink off a resolved style (the chart's own element). */
export function readChartInk(read: (token: string) => string): ChartInk {
  const ink: Record<string, string | number> = {}
  for (const [part, token] of Object.entries(CHART_INK_TOKENS)) {
    const value = read(token).trim()
    if (!value) continue
    if (part === 'size' || part === 'tipSize' || part === 'radius') {
      const px = Number.parseFloat(value)
      if (Number.isFinite(px) && /px$/u.test(value)) ink[part] = px
    } else {
      ink[part] = value
    }
  }
  return ink as ChartInk
}

/** Drop every part the ink did not resolve, so an unthemed host (a unit
 *  test's DOM, a page with no ThemeProvider) leaves ECharts' own defaults
 *  rather than receiving empty objects that look like decisions. */
const prune = (value: Record<string, unknown>): Record<string, unknown> | undefined => {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value)) {
    const kept = v !== null && typeof v === 'object' && !Array.isArray(v)
      ? prune(v as Record<string, unknown>) : v
    if (kept !== undefined && kept !== '') out[k] = kept
  }
  return Object.keys(out).length ? out : undefined
}

/**
 * The option fragment that paints the chart's chrome in the kit's ink. A
 * fragment, so a theme change is one merge onto a live chart rather than a
 * rebuild of the data.
 */
export function chartInkOption(ink: ChartInk): EChartsCoreOption {
  const text = { fontFamily: ink.font, color: ink.text, fontSize: ink.size }
  const axis = {
    axisLabel: text,
    axisLine: { lineStyle: { color: ink.text } },
    axisTick: { lineStyle: { color: ink.line } },
    splitLine: { lineStyle: { color: ink.line } },
  }
  return (prune({
    textStyle: { fontFamily: ink.font, color: ink.text },
    legend: { textStyle: text },
    xAxis: axis,
    yAxis: axis,
    tooltip: {
      backgroundColor: ink.plate,
      borderColor: ink.line,
      textStyle: { fontFamily: ink.font, color: ink.strong, fontSize: ink.tipSize },
      extraCssText: [
        ink.radius !== undefined ? `border-radius: ${ink.radius}px;` : '',
        ink.shadow ? `box-shadow: ${ink.shadow};` : '',
      ].join(' ').trim(),
    },
  }) ?? {}) as EChartsCoreOption
}

/**
 * Translate Marble's finite grammar into the small ECharts subset we ship.
 * This function accepts no ECharts option from DATA. Null remains null, so a
 * missing heartbeat paints a gap rather than a fictional zero.
 */
export function buildChartOption(
  definition: ChartDefinition,
  rows: readonly InsightDatum[],
  palette: readonly string[] = DEFAULT_PALETTE,
  reducedMotion = false,
  accessibleDescription?: string,
  seriesKinds?: Readonly<Record<string, 'line' | 'area' | 'bar'>>,
  ink: ChartInk = {},
): EChartsCoreOption {
  const problem = chartAdapterProblem(definition)
  if (problem) throw new Error(problem)
  const categoryField = definition.encoding.timeField ?? definition.encoding.dimension
  // chartAdapterProblem has proved this; the fallback keeps TypeScript honest.
  if (!categoryField) throw new Error('InsightChart needs a governed time or dimension encoding')

  // Sorting precedes limiting: a governed "top 10" must not mean the first
  // ten rows returned by an otherwise unordered transport.
  const input = orderedRows(definition, rows).slice(0, definition.limit ?? 500)
  const categories = [...new Set(input.map((row) => key(row[categoryField])))]
  const seriesField = definition.encoding.series
  const groups = seriesField
    ? [...new Set(input.map((row) => key(row[seriesField])))]
    : ['']
  /* Every series is named by ITS OWN measure. The comparison used to be named
     by a hard-coded ' allocated' suffix — the word of the one seat chart this
     adapter was first written for — so a revenue-vs-target chart put
     "allocated" in its legend and tooltip, and an ungrouped comparison was
     titled "allocated" outright (design-sync kit finding, InsightChart:155).
     The grammar carries no display label, so the governed field name is the
     label, read as words; a grouped series is the group, and a group's second
     and third measures say which measure they are. */
  const measures = [
    { field: definition.encoding.measure, role: 'measure' as const },
    ...(definition.encoding.comparisonMeasure
      ? [{ field: definition.encoding.comparisonMeasure, role: 'comparison' as const }]
      : []),
    ...(definition.encoding.baselineMeasure
      ? [{ field: definition.encoding.baselineMeasure, role: 'baseline' as const }]
      : []),
  ]

  const bars = ['bar', 'stacked_bar', 'bullet', 'waterfall']
  const chartType = bars.includes(definition.kind) ? 'bar' : 'line'
  const sparkline = definition.kind === 'sparkline'
  const series = groups.flatMap((group) => measures.map((measure) => {
    const values = categories.map((category) => {
      const row = input.find((candidate) =>
        key(candidate[categoryField]) === category
        && (!seriesField || key(candidate[seriesField]) === group))
      return row ? number(row[measure.field]) : null
    })
    const name = measure.role === 'measure'
      ? group || fieldWords(measure.field)
      : group ? `${group} · ${fieldWords(measure.field)}` : fieldWords(measure.field)
    /* Timeline is the one governed chart that legitimately mixes encodings:
       counts are columns, while numeric readings are continuous lines/areas.
       The map is renderer input supplied by a trusted host from the finite
       aggregation vocabulary; it is not part of the stored chart definition
       and cannot smuggle an ECharts option into this adapter. */
    const requestedKind = seriesKinds?.[group]
    const pointType = requestedKind === 'bar' ? 'bar' : requestedKind ? 'line' : chartType
    return {
      name,
      type: pointType,
      data: values,
      connectNulls: false,
      showSymbol: values.length <= 40,
      symbolSize: 7,
      ...(definition.kind === 'area' || requestedKind === 'area'
        ? { areaStyle: { opacity: .14 } } : {}),
      ...(definition.kind === 'stacked_bar' ? { stack: 'total' } : {}),
      /* A sparkline is a line stripped of everything that explains it: it lives
         beside a figure that already carries the meaning, so axes and symbols
         would repeat what the number says and crowd a small tile. */
      ...(sparkline ? { showSymbol: false, lineStyle: { width: 2 }, symbol: 'none' } : {}),
      /* A bullet compares one value with its target. The measure is the bar and
         the COMPARISON is a line across it, because a second bar invites the
         eye to read two independent quantities rather than one against the
         other. */
      ...(definition.kind === 'bullet' && measure.role === 'measure'
        ? { barWidth: '46%' } : {}),
      ...(definition.kind === 'bullet' && measure.role !== 'measure'
        ? { type: 'line', symbol: 'rect', symbolSize: [22, 3], lineStyle: { width: 0 } } : {}),
      /* A waterfall stacks each step on an invisible running base, so the bars
         float where the total stands rather than all starting at zero. */
      ...(definition.kind === 'waterfall' ? { stack: 'total' } : {}),
    }
  }))

  /* A LENGTH ENCODES THE VALUE, so its axis starts at zero. `scale: true` lets
     ECharts fit the axis to the data's own range, which is right for a line —
     a line encodes change, and a 96..104 band is where its shape lives — and a
     lie for a bar: two bars of 96 and 104 drawn on a 95..105 axis read as one
     being nine times the other. It was set for EVERY kind (design-sync kit
     finding, InsightChart:233), so bar, stacked, bullet and waterfall all
     floated. An area is a length too — its fill runs down to the axis floor —
     so it is held to zero with the bars; so is any series a host asked to draw
     as bars (Timeline's counts). Only a bare line keeps the fitted axis. */
  const encodesLength = chartType === 'bar'
    || definition.kind === 'area'
    || Object.values(seriesKinds ?? {}).some((kind) => kind === 'bar' || kind === 'area')
  /* The governed format reaches the value axis and the tooltip: a currency
     chart said `50000`, never `€50,000` (definition.format was never read). */
  const formatValue = chartValueFormatter(definition.format)
  /* A time axis speaks dates, not ISO keys. `categories` stays the raw key —
     `rowForChartPoint` maps a point back to its row through it — and only the
     LABELS the axis draws are formatted. */
  const timeGrain = definition.encoding.timeField ? definition.encoding.timeGrain : undefined
  const categoryLabels = timeGrain
    ? categories.map((category) => formatTimeBucket(category, timeGrain))
    : categories

  /* The chrome in the kit's ink (see ChartInk). Merged part by part below, so
     a grammar setting on the same part (the value formatter, a sparkline's
     stripped axes) still wins over the paint. */
  const paint = chartInkOption(ink) as {
    textStyle?: object; legend?: object; tooltip?: object
    xAxis?: { axisLabel?: object }; yAxis?: { axisLabel?: object }
  }
  const yLabel = { ...paint.yAxis?.axisLabel, ...(formatValue ? { formatter: formatValue } : {}) }

  return {
    animation: !reducedMotion,
    textStyle: paint.textStyle,
    animationDuration: reducedMotion ? 0 : 280,
    color: [...palette],
    aria: {
      enabled: true,
      ...(accessibleDescription ? { description: accessibleDescription } : {}),
      decal: { show: true },
    },
    grid: sparkline
      ? { left: 0, right: 0, top: 2, bottom: 2, containLabel: false }
      : { left: 12, right: 12, top: groups.length > 1 ? 48 : 20, bottom: 8, containLabel: true },
    tooltip: {
      ...paint.tooltip,
      trigger: 'axis',
      // Inside the chart's own box: at phone width the tooltip otherwise
      // opened past the card's left edge, cut by the viewport.
      confine: true,
      ...(formatValue
        ? { valueFormatter: (value: unknown) => typeof value === 'number' ? formatValue(value) : String(value ?? '') }
        : {}),
    },
    legend: { ...paint.legend, show: !sparkline && series.length > 1, top: 0, type: 'scroll' },
    xAxis: {
      ...paint.xAxis,
      type: 'category',
      data: categoryLabels,
      boundaryGap: chartType === 'bar' || Object.values(seriesKinds ?? {}).includes('bar'),
      axisLabel: { ...paint.xAxis?.axisLabel, hideOverlap: true },
      // Stripped, not merely hidden: a sparkline that reserved axis room would
      // draw a smaller line in the same box for no reader's benefit.
      ...(sparkline
        ? { show: false, axisLine: { show: false }, axisTick: { show: false } }
        : {}),
    },
    yAxis: {
      ...paint.yAxis,
      type: 'value',
      scale: !encodesLength,
      ...(Object.keys(yLabel).length ? { axisLabel: yLabel } : {}),
      ...(sparkline ? { show: false } : {}),
    },
    series: waterfallSeries(definition, series, categories),
  } as EChartsCoreOption
}

/** Refuse an invalid composed-kind definition even if a host skipped validation. */
function assertComposedEncoding(definition: ChartDefinition) {
  if (definition.kind !== 'waterfall' && definition.kind !== 'bullet') return
  const { dimension, timeField, series, comparisonMeasure, baselineMeasure } = definition.encoding
  const axes = [dimension, timeField].filter((field) => field !== undefined)
  if (axes.length !== 1) {
    throw new Error(`${definition.kind} needs exactly one governed category axis`)
  }
  if (series !== undefined) throw new Error(`${definition.kind} does not support grouped series`)
  if (baselineMeasure !== undefined) throw new Error(`${definition.kind} does not support a baseline measure`)
  if (definition.kind === 'waterfall' && comparisonMeasure !== undefined) {
    throw new Error('waterfall does not support a comparison measure')
  }
  if (definition.kind === 'bullet' && comparisonMeasure === undefined) {
    throw new Error('bullet needs one comparison measure as its target')
  }
  if (definition.kind === 'bullet' && comparisonMeasure === definition.encoding.measure) {
    throw new Error('bullet target must differ from its measure')
  }
}

/**
 * A waterfall's invisible base.
 *
 * Each step floats on the running total before it, so the bars show where the
 * total stands rather than all starting at zero. The base is a real stacked
 * series with no colour — ECharts has no waterfall type, and drawing one as a
 * chart type it does not have would mean registering another series for a shape
 * Bar already makes.
 *
 * Nulls stay gaps: a missing observation must not silently contribute nothing
 * to a running total. Once one delta is missing, that total and every later
 * offset remain unknown; this grammar defines no implicit restart point.
 */
function waterfallSeries(
  definition: ChartDefinition,
  series: readonly Record<string, unknown>[],
  categories: readonly string[],
): Record<string, unknown>[] {
  if (definition.kind !== 'waterfall' || series.length === 0) return [...series]
  const first = series[0] as { data: (number | null)[] }
  const base: (number | null)[] = []
  const deltas: (number | null)[] = []
  let running = 0
  let known = true
  for (let index = 0; index < categories.length; index += 1) {
    const step = first.data[index]
    if (step == null) known = false
    if (!known || step == null) {
      base.push(null)
      deltas.push(null)
      continue
    }
    base.push(running)
    deltas.push(step)
    running += step
  }
  return [
    {
      name: 'running total',
      type: 'bar',
      stack: 'total',
      /* ECharts otherwise stacks positive and negative values separately. The
         delta must begin at the preceding running total in either direction. */
      stackStrategy: 'all',
      data: base,
      /* No decal either. `aria.decal.show` hatches every series so colour is
         not the only cue — including this one, whose fill is transparent, so
         the invisible base drew as a hatched block under every step (design-
         sync kit finding, InsightChart:215/304). `symbol: 'none'` is ECharts'
         per-series opt-out; the visible deltas keep theirs. */
      itemStyle: { color: 'transparent', decal: { symbol: 'none' } },
      // Not a quantity anybody should read, so it stays out of the legend and
      // out of the tooltip rather than appearing as a mysterious series.
      silent: true,
      tooltip: { show: false },
      legendHoverLink: false,
    },
    {
      ...first,
      data: deltas,
      stackStrategy: 'all',
    },
  ]
}

export interface InsightChartProps {
  definition: ChartDefinition
  rows: readonly InsightDatum[]
  /** Meaningful conclusion, also exposed by the containing InsightCard. */
  accessibleDescription: string
  /** A visual point reports its governed source row; routing remains the host's. */
  onActivate?: (row: InsightDatum) => void
  /**
   * A finite per-series visual role, used by Timeline to keep row counts as
   * bars and numeric readings as lines/areas. Keys are values of the governed
   * `encoding.series` field; arbitrary chart options remain impossible.
   */
  seriesKinds?: Readonly<Record<string, 'line' | 'area' | 'bar'>>
  /**
   * The chart's box. `default` is the full chart (15-25rem, fluid). `small` is
   * the 3rem strip a `sparkline` needs beside the figure it qualifies — before
   * it existed a sparkline filled a full chart box, because the box was fixed
   * and the kind only stripped the axes inside it. A governed step, not a
   * pixel height: a host cannot size a chart to a number of its choosing.
   */
  size?: InsightChartSize
  testId?: string
}

export type InsightChartSize = 'default' | 'small'

/** Lazy, SVG-first adapter. It renders no data source and owns no drill act. */
export function InsightChart({
  definition, rows, accessibleDescription, onActivate, seriesKinds, size = 'default', testId,
}: InsightChartProps) {
  const host = useRef<HTMLDivElement>(null)
  const chart = useRef<EChartsType | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

  useEffect(() => {
    const node = host.current
    if (!node || typeof ResizeObserver === 'undefined') return
    let cancelled = false
    let observer: ResizeObserver | null = null
    let themeWatch: MutationObserver | null = null
    setFailure(null)

    void loadEngine()
      .then((echarts) => {
        if (cancelled || !host.current) return
        const paletteOf = (styles: CSSStyleDeclaration) => ['--brand', '--good', '--info', '--warn', '--crit']
          .map((token) => styles.getPropertyValue(token).trim()).filter(Boolean)
        const styles = getComputedStyle(host.current)
        const palette = paletteOf(styles)
        chart.current = echarts.init(host.current, undefined, { renderer: 'svg' })
        chart.current.setOption(buildChartOption(
          definition,
          rows,
          palette.length ? palette : undefined,
          reducedMotion,
          accessibleDescription,
          seriesKinds,
          readChartInk((token) => styles.getPropertyValue(token)),
        ), true)
        /* THE THEME CAN FLIP UNDER A LIVE CHART. ThemeProvider writes the
           tokens and `data-theme` onto <html>; the engine holds resolved
           values, so it is told again — one merge of the chrome and the
           palette, never a rebuild of the data. */
        if (typeof MutationObserver !== 'undefined') {
          themeWatch = new MutationObserver(() => {
            if (!chart.current || !host.current) return
            const now = getComputedStyle(host.current)
            const hues = paletteOf(now)
            chart.current.setOption({
              ...(hues.length ? { color: hues } : {}),
              ...chartInkOption(readChartInk((token) => now.getPropertyValue(token))),
            })
          })
          themeWatch.observe(document.documentElement, {
            attributes: true, attributeFilter: ['data-theme', 'style', 'class'],
          })
        }
        if (onActivate) {
          chart.current.on('click', (point: ECElementEvent) => {
            const row = rowForChartPoint(definition, rows, point)
            if (row) onActivate(row)
          })
        }
        observer = new ResizeObserver(() => chart.current?.resize())
        observer.observe(host.current)
      })
      .catch(() => {
        if (cancelled) return
        chart.current?.dispose()
        chart.current = null
        setFailure(`The ${definition.kind} chart could not be drawn.`)
      })

    return () => {
      cancelled = true
      observer?.disconnect()
      themeWatch?.disconnect()
      chart.current?.dispose()
      chart.current = null
    }
  }, [accessibleDescription, definition, onActivate, reducedMotion, rows, seriesKinds])

  return (
    <>
      {failure
        ? <p role="alert" data-testid={testId ? `${testId}-error` : undefined}>{failure}</p>
        : null}
      <div
        ref={host}
        className={s.chart}
        role={failure ? undefined : 'img'}
        aria-label={failure ? undefined : accessibleDescription}
        aria-hidden={failure ? true : undefined}
        hidden={failure != null}
        data-testid={failure ? undefined : testId}
        data-chart-kind={definition.kind}
        data-size={size}
      />
    </>
  )
}

/* Re-declared beside the component whose props are written in it, so a host
   can type the data it hands this component from `@solum/ui` alone (#2406,
   design-sync kit pass: "hosts need @solum/experience to type their data").
   An alias, not a copy — the shape stays Experience's. */
export type ChartDefinition = GovernedChartDefinition
